import * as React from 'react'
import clx from 'classnames'

interface SettingsSectionWithTabsProps {
  className?: string
  classNameNavigation?: string
  /** Title above the local tabs */
  title?: string
  /** Optional default selected tab name */
  selected?: string
  setSelected?: React.Dispatch<React.SetStateAction<string>>
  /** Tabs as children (SettingsSectionTab elements) */
  children?: React.ReactNode
  fullWidth?: boolean
}

/** Individual tab component */
export interface SettingsSectionTabProps {
  /** Unique name of the tab */
  name: string
  /** Display title */
  title: string
  /** Tab content */
  children: React.ReactNode
}

/**
 * Tab component to be used inside SettingsSectionWithTabs.
 */
export const SettingsSectionTab: React.FC<SettingsSectionTabProps> = ({
  children,
}) => {
  return <>{children}</>
}

/**
 * Section component that groups multiple local tabs together.
 */
export const SettingsSectionWithTabs: React.FC<
  SettingsSectionWithTabsProps
> = ({ fullWidth = false, title, selected, setSelected, className, children, classNameNavigation }) => {
  // Collect tab props from children
  const tabs = React.Children.toArray(
    children,
  ) as React.ReactElement<SettingsSectionTabProps>[]
  const firstTab = tabs[0]?.props.name || tabs[0]?.props['data-tab-name'] || 0
  const [activeTab, setActiveTab] = React.useState<string>(selected || firstTab)

  const activeContent = React.useMemo(() => {
    return tabs.find(
      (tab) =>
        tab.props.name === activeTab ||
        tab.props['data-tab-name'] === activeTab,
    ) //?.props.children;
  }, [activeTab, tabs])

  return (
    <section className={clx("tw-mb-10 tw-flex-1  tw-p-2 tw-shadow-sm lg:tw-flex lg:tw-gap-4", className)}>
      {/* Header */}
      <header className={clx("tw-mb-4 tw-flex tw-flex-1 tw-flex-col tw-gap-2 tw-pt-4 lg:tw-max-w-[300px] lg:tw-items-end", classNameNavigation)}>
        {title && (
          <h2 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-gray-900">
            {title}
          </h2>
        )}

        <div className="tw-flex tw-flex-col tw-gap-5 tw-p-2 tw-pb-2 lg:tw-items-end">
          {tabs.map((tab, i) => {
            const isActive = activeTab
              ? tab.props.name === activeTab ||
                tab.props['data-tab-name'] === activeTab
              : i === 0
            const name =
              tab.props.name ||
              tab.props['data-tab-name']
            return (
              <button
                key={name}
                onClick={() => {
                  setSelected && setSelected(name)
                  setActiveTab(name)
                }}
                className={clx(
                  'px-1 py-1.5 text-sm font-medium transition-colors lg:tw-text-right',
                  {
                    'tw-border-b-2 tw-border-primary tw-text-primary': isActive,
                    'tw-text-gray-500 hover:tw-text-gray-700': !isActive,
                  },
                )}
              >
                {tab.props.title}
              </button>
            )
          })}
        </div>
      </header>

      {/* Content */}
      <div className={clx("tw-flex-[3] tw-space-y-5 tw-pt-4", {
        "lg:tw-max-w-[900px]": !fullWidth,
      })}>
        {activeContent}
      </div>
    </section>
  )
}
